import { useState, useEffect } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const TaxonomyView = ({ loaded }) => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    apiFetch('/api/invoices-v2/taxonomies')
      .then((res) => res.json())
      .then((data) => setTaxonomies(data.taxonomies || []))
      .catch(() => setTaxonomies([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-1">Vendor Taxonomies</h1>
        <p className="text-sm text-zinc-400">
          AI-generated category taxonomies per vendor, used to ensure consistent spend classification
        </p>
      </div>

      {/* Stats */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '350ms',
        }}
      >
        <StatCard label="Vendors" value={taxonomies.length} />
        <StatCard
          label="Total Categories"
          value={taxonomies.reduce((sum, t) => sum + Object.keys(t.taxonomy?.categories || {}).length, 0)}
        />
        <StatCard
          label="Invoices Processed"
          value={taxonomies.reduce((sum, t) => sum + (t.invoice_count || 0), 0)}
        />
      </div>

      {/* Taxonomy list */}
      <div
        className="space-y-3"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1)',
          transitionDelay: '400ms',
        }}
      >
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
          </div>
        ) : taxonomies.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <p className="text-sm text-zinc-400">
              No taxonomies yet. Process an invoice to generate a vendor taxonomy automatically.
            </p>
          </div>
        ) : (
          taxonomies.map((tax) => (
            <TaxonomyCard
              key={tax.id}
              taxonomy={tax}
              expanded={expandedId === tax.id}
              onToggle={() => setExpandedId(expandedId === tax.id ? null : tax.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

/* ─── Stat card ─── */
const StatCard = ({ label, value }) => (
  <div className="bg-white border border-zinc-200 rounded-lg px-4 py-3">
    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1">{label}</p>
    <p className="text-xl font-light text-zinc-900">{value}</p>
  </div>
);

/* ─── Taxonomy card ─── */
const TaxonomyCard = ({ taxonomy, expanded, onToggle }) => {
  const tax = taxonomy.taxonomy || {};
  const categories = tax.categories || {};
  const categoryNames = Object.keys(categories);
  const vendorName = taxonomy.vendors?.display_name || 'Unknown Vendor';

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50/50 transition-colors"
      >
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
          className={`text-zinc-400 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        >
          <path d="M4 2l4 4-4 4" />
        </svg>

        <div className="flex-1 min-w-0 flex items-center gap-4">
          <span className="text-sm font-medium text-zinc-900 truncate">{vendorName}</span>
          {tax.domain && (
            <span className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded hidden sm:inline">
              {tax.domain}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-zinc-400">{categoryNames.length} categories</span>
          <span className="text-xs text-zinc-400 hidden sm:inline">
            {taxonomy.invoice_count || 1} invoice{taxonomy.invoice_count !== 1 ? 's' : ''}
          </span>
          {tax.version && (
            <span className="text-[10px] text-zinc-300">v{tax.version}</span>
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-zinc-100">
          {/* Domain header */}
          {tax.domain && (
            <div className="px-4 py-2 bg-zinc-50/50 border-b border-zinc-100 flex items-center justify-between">
              <span className="text-xs text-zinc-500">
                Domain: <span className="text-zinc-700 font-medium">{tax.domain}</span>
              </span>
              <span className="text-[10px] text-zinc-300">
                Updated {new Date(taxonomy.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}

          {/* Category grid */}
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {categoryNames.map((catName) => {
              const cat = categories[catName];
              const subcats = cat.subcategories || [];

              return (
                <div
                  key={catName}
                  className="border border-zinc-100 rounded-lg p-3 hover:border-zinc-200 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-zinc-900">{catName}</h4>
                    <span className="text-[10px] text-zinc-300 shrink-0 ml-2">{subcats.length}</span>
                  </div>
                  {cat.description && (
                    <p className="text-[11px] text-zinc-400 mb-2 leading-relaxed">{cat.description}</p>
                  )}
                  {subcats.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {subcats.map((sub) => (
                        <span
                          key={sub}
                          className="text-[10px] bg-zinc-50 text-zinc-600 px-1.5 py-0.5 rounded border border-zinc-100"
                        >
                          {sub}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxonomyView;
